import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminGetJobRecords } from '../../lib/adminApi';
import AdminSidebar from '../../components/AdminSidebar';

const NAVY   = '#0a2540';
const ORANGE = '#F37428';
const BLUE   = '#0176CE';

function fmt$(n) { return n == null ? '—' : '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
function fmtDate(raw) { if (!raw) return '—'; return new Date(raw).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }

function ConfBadge({ val }) {
  const n = parseFloat(val);
  if (isNaN(n)) return null;
  const pct = n > 1 ? Math.round(n) : Math.round(n * 100);
  const color = pct >= 90 ? '#059669' : pct >= 70 ? '#d97706' : '#dc2626';
  return <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 5px', borderRadius: 4, background: `${color}18`, color }}>{pct}%</span>;
}

export default function AdminJobRecords() {
  const { tenantId, jobId } = useParams();
  const navigate = useNavigate();

  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [page, setPage]       = useState(1);
  const [expandedRow, setExpandedRow] = useState(null);

  useEffect(() => {
    setLoading(true);
    adminGetJobRecords(jobId, page)
      .then(res => { setData(res); setLoading(false); })
      .catch(e  => { setError(e.message); setLoading(false); });
  }, [jobId, page]);

  const job     = data?.job     || {};
  const records = data?.records || [];
  const total   = data?.total   || 0;
  const pageSize = data?.page_size || 50;
  const totalPages = Math.ceil(total / pageSize);

  return (
    <div style={s.page}>
      <AdminSidebar active="tenants" />
      <main style={s.main}>
        <div style={s.header}>
          <button style={s.backBtn} onClick={() => navigate(`/admin/tenants/${tenantId}`)}>← Back to Tenant</button>
          {!loading && !error && (
            <>
              <h1 style={s.title}>{job.file_name || `Job #${jobId}`}</h1>
              <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginTop: 8 }}>
                <span style={s.meta}>Records: <strong>{(job.record_count||0).toLocaleString()}</strong></span>
                <span style={s.meta}>Enriched: <strong>{(job.processed_count||0).toLocaleString()}</strong></span>
                <span style={s.meta}>Status: <strong style={{ textTransform: 'capitalize' }}>{job.status}</strong></span>
                <span style={s.meta}>Submitted: <strong>{fmtDate(job.created_on_utc)}</strong></span>
                {job.amount_usd && <span style={s.meta}>Revenue: <strong style={{ color: '#059669' }}>{fmt$(job.amount_usd)}</strong></span>}
              </div>
              <p style={{ color: '#94a3b8', fontSize: 12, margin: '4px 0 0' }}>Showing {records.length} of {total.toLocaleString()} records (page {page} of {totalPages})</p>
            </>
          )}
        </div>

        {loading && <div style={{ color: '#64748b', padding: 32 }}>Loading…</div>}
        {error   && <div style={{ color: '#ef4444', padding: 32 }}>{error}</div>}

        {!loading && !error && (
          <>
            <div style={s.card}>
              <table style={s.table}>
                <thead>
                  <tr>
                    {['#', 'Input Company', 'Clean Name', 'Domain', 'City', 'NAICS', 'Profile Source', 'Tokens', 'Confidence', 'Status'].map(h =>
                      <th key={h} style={s.th}>{h}</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {records.map(r => (
                    <>
                      <tr
                        key={r.row_id || r.row_number}
                        style={{ ...s.tr, cursor: r.profile_description ? 'pointer' : 'default', background: expandedRow === r.row_id ? 'rgba(1,118,206,0.04)' : undefined }}
                        onClick={() => setExpandedRow(expandedRow === r.row_id ? null : r.row_id)}
                      >
                        <td style={{ ...s.td, color: '#94a3b8', fontSize: 11, width: 40 }}>{r.row_number}</td>
                        <td style={{ ...s.td, color: '#64748b', fontSize: 12, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.input_company_name}>{r.input_company_name || '—'}</td>
                        <td style={{ ...s.td, fontWeight: 500, color: NAVY, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.clean_company_name}>{r.clean_company_name || '—'}</td>
                        <td style={{ ...s.td, fontSize: 12, maxWidth: 130 }}>
                          {r.domain ? <a href={`https://${r.domain}`} target="_blank" rel="noreferrer" style={{ color: BLUE, textDecoration: 'none' }}>{r.domain}</a> : '—'}
                        </td>
                        <td style={{ ...s.td, color: '#64748b', fontSize: 12 }}>{[r.city, r.state].filter(Boolean).join(', ') || '—'}</td>
                        <td style={{ ...s.td, fontSize: 12 }}>{r.naics_code ? <span title={r.naics_description}>{r.naics_code}</span> : '—'}</td>
                        <td style={{ ...s.td, fontSize: 11 }}>
                          {r.profile_source ? (
                            <span style={{
                              background: r.profile_source.includes('SONNET') ? 'rgba(243,116,40,0.08)' : 'rgba(1,118,206,0.08)',
                              color: r.profile_source.includes('SONNET') ? ORANGE : BLUE,
                              borderRadius: 4, padding: '1px 6px', fontSize: 10, fontWeight: 600,
                            }}>
                              {r.profile_source.includes('SONNET') ? 'Sonnet' : r.profile_source.includes('HAIKU') ? 'Haiku' : r.profile_source}
                            </span>
                          ) : '—'}
                        </td>
                        <td style={{ ...s.td, fontSize: 12, textAlign: 'right' }}>{r.total_tokens ? r.total_tokens.toLocaleString() : '—'}</td>
                        <td style={s.td}><ConfBadge val={r.confidence_score} /></td>
                        <td style={s.td}>
                          <span style={{
                            background: r.status === 'complete' ? 'rgba(5,150,105,0.08)' : r.status === 'error' ? 'rgba(239,68,68,0.08)' : 'rgba(100,116,139,0.08)',
                            color: r.status === 'complete' ? '#059669' : r.status === 'error' ? '#dc2626' : '#64748b',
                            borderRadius: 4, fontSize: 10, fontWeight: 600, padding: '2px 6px', textTransform: 'uppercase',
                          }}>{r.status || '—'}</span>
                        </td>
                      </tr>
                      {expandedRow === r.row_id && r.profile_description && (
                        <tr key={`${r.row_id}-exp`} style={{ background: 'rgba(1,118,206,0.03)' }}>
                          <td colSpan={10} style={{ padding: '12px 20px 16px', borderBottom: '1px solid #f1f5f9' }}>
                            <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.6, maxWidth: 800 }}>
                              <strong style={{ color: NAVY, fontSize: 12, display: 'block', marginBottom: 4 }}>Profile</strong>
                              {r.profile_description}
                            </div>
                            {r.error_message && (
                              <div style={{ marginTop: 8, fontSize: 12, color: '#dc2626' }}>
                                <strong>Error:</strong> {r.error_message}
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </>
                  ))}
                  {records.length === 0 && (
                    <tr><td colSpan={10} style={{ ...s.td, textAlign: 'center', color: '#94a3b8', padding: 32 }}>No records found</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 20 }}>
                <button style={s.pageBtn} disabled={page <= 1} onClick={() => setPage(1)}>«</button>
                <button style={s.pageBtn} disabled={page <= 1} onClick={() => setPage(p => p - 1)}>‹</button>
                <span style={{ fontSize: 13, color: '#64748b' }}>Page {page} of {totalPages}</span>
                <button style={s.pageBtn} disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>›</button>
                <button style={s.pageBtn} disabled={page >= totalPages} onClick={() => setPage(totalPages)}>»</button>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}

const s = {
  page:    { display: 'flex', minHeight: '100vh', fontFamily: "'Inter', system-ui, sans-serif" },
  main:    { flex: 1, background: '#f0f5fb', padding: '36px 40px', overflowY: 'auto' },
  header:  { marginBottom: 24 },
  backBtn: { background: 'transparent', border: 'none', color: BLUE, cursor: 'pointer', fontSize: 13, fontWeight: 500, padding: '0 0 8px', display: 'block' },
  title:   { color: NAVY, fontSize: 22, fontWeight: 700, margin: 0, letterSpacing: '-0.3px' },
  meta:    { color: '#64748b', fontSize: 13 },
  card:    { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'auto', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' },
  table:   { width: '100%', borderCollapse: 'collapse', minWidth: 900 },
  th:      { color: '#94a3b8', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '10px 14px', textAlign: 'left', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' },
  tr:      { borderBottom: '1px solid #f1f5f9' },
  td:      { color: '#374151', fontSize: 13, padding: '10px 14px', verticalAlign: 'middle' },
  pageBtn: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6, color: NAVY, cursor: 'pointer', fontSize: 13, padding: '6px 12px' },
};
